import { useState, useRef } from 'react';
import { Globe, ArrowLeft, ArrowRight, RefreshCw, Home, Lock } from 'lucide-react';

export function BrowserApp() {
  const [url, setUrl] = useState('https://example.com');
  const [currentUrl, setCurrentUrl] = useState('https://example.com');
  const [history, setHistory] = useState<string[]>(['https://example.com']);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const frameRef = useRef<HTMLIFrameElement>(null);

  const navigate = (nextUrl: string) => {
    let clean = nextUrl.trim();
    if (!/^https?:\/\//i.test(clean)) {
      clean = 'https://' + clean;
    }
    setUrl(clean);
    setCurrentUrl(clean);
    setLoading(true);
    const newHistory = [...history.slice(0, historyIndex + 1), clean];
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
    setTimeout(() => setLoading(false), 300);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') navigate(url);
  };

  const goBack = () => {
    if (historyIndex > 0) {
      const idx = historyIndex - 1;
      setHistoryIndex(idx);
      setCurrentUrl(history[idx]);
      setUrl(history[idx]);
    }
  };

  const goForward = () => {
    if (historyIndex < history.length - 1) {
      const idx = historyIndex + 1;
      setHistoryIndex(idx);
      setCurrentUrl(history[idx]);
      setUrl(history[idx]);
    }
  };

  const isSecure = currentUrl.startsWith('https://');

  return (
    <div className="h-full flex flex-col bg-desktop-surface2">
      {/* Toolbar */}
      <div className="flex items-center gap-1 px-2 py-1.5 bg-desktop-surface border-b border-desktop-border shrink-0">
        <div className="flex items-center gap-0.5">
          <button onClick={goBack} disabled={historyIndex === 0} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-desktop-hover disabled:opacity-30 text-desktop-text/70 transition-colors">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <button onClick={goForward} disabled={historyIndex >= history.length - 1} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-desktop-hover disabled:opacity-30 text-desktop-text/70 transition-colors">
            <ArrowRight className="w-4 h-4" />
          </button>
          <button onClick={() => navigate(currentUrl)} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-desktop-hover text-desktop-text/70 transition-colors">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={() => navigate('https://www.google.com')} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-desktop-hover text-desktop-text/70 transition-colors" title="Home">
            <Home className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="flex-1 flex items-center gap-1.5 mx-2 px-3 py-1.5 bg-desktop-surface2 border border-desktop-border rounded-lg min-w-0">
          {isSecure ? <Lock className="w-3 h-3 text-desktop-success shrink-0" /> : <Globe className="w-3 h-3 text-desktop-warning shrink-0" />}
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 bg-transparent text-xs text-desktop-text focus:outline-none"
            spellCheck={false}
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 bg-white">
        <iframe
          ref={frameRef}
          src={currentUrl}
          className="w-full h-full border-0"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals allow-downloads"
          referrerPolicy="no-referrer"
        />
      </div>
    </div>
  );
}